import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { DEFAULT_BRANDING, resolveFont, type Branding } from '../branding';
import { siteApi } from '../api/site';

interface BrandingCtx {
  branding: Branding;
  /** Ayarlar kaydedildikten sonra çağır — tüm uygulamaya anında yansır. */
  refresh: () => Promise<void>;
}

const Ctx = createContext<BrandingCtx | null>(null);

/** Marka ayarlarını uygular: font (--sans), sekme başlığı, gerekiyorsa Google Font. */
function applyBranding(b: Branding) {
  const font = resolveFont(b.font);
  document.documentElement.style.setProperty('--sans', font.stack);

  const id = 'brand-font';
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (font.google) {
    const href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`;
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  } else if (link) {
    link.remove();
  }

  document.title = b.siteTitle || b.appName || DEFAULT_BRANDING.siteTitle;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);

  const refresh = useCallback(async () => {
    try {
      const site = await siteApi.get();
      const merged: Branding = { ...DEFAULT_BRANDING, ...site };
      setBranding(merged);
      applyBranding(merged);
    } catch {
      // Ayar okunamazsa varsayılan markayla devam et.
      applyBranding(DEFAULT_BRANDING);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <Ctx.Provider value={{ branding, refresh }}>{children}</Ctx.Provider>;
}

export function useBranding(): BrandingCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useBranding, BrandingProvider içinde kullanılmalı');
  return ctx;
}
